import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import SearchPreviewNew from './SearchPreviewNew';
import { useAuth } from './AuthContext';
import SearchService from '../services/SearchService';

const SearchBar = ({ setData, startDate, endDate, setLoading, setNoResults, onSearch, setAnswer }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const initialQuery = searchParams.get('q') || '';

    const [inputValue, setInputValue] = useState(initialQuery);
    const searchBoxRef = useRef(null);
    const searchInputRef = useRef(null);
    const { userIsPremium, userId, flaskServerAddress, serverAddress } = useAuth();
    const searchService = useRef(new SearchService(flaskServerAddress, serverAddress)).current;
    const isClickingPreviewItemRef = useRef(false);

    const [showPreview, setShowPreview] = useState(false);

    const fetchData = async (query) => {
        try {
            if (!query) return;
            
            setLoading(true);
            const result = await searchService.search(query, {
                startDate,
                endDate,
                userId
            });

            if (result) {
                if (result.type === 'question') {
                    setAnswer(result.answer);
                    setData(result.posts);
                } else {
                    setData(result.posts);
                }
                setNoResults(result.noResults);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputFocus = () => {
        setShowPreview(true);
    };
    
    const handleInputBlur = () => {
        if (isClickingPreviewItemRef.current) {
            isClickingPreviewItemRef.current = false;
            return;
        }
        setTimeout(() => {
            if (!searchBoxRef.current?.contains(document.activeElement) && 
                !document.querySelector('.search-preview-container')?.contains(document.activeElement)) {
                 setShowPreview(false);
            }
        }, 150);
    };

    const handleSearch = () => {
        onSearch(inputValue);
        setShowPreview(false);
        if (searchInputRef.current) {
            searchInputRef.current.blur();
        }
    };

    const handleInputChange = (event) => {
        setInputValue(event.target.value);
        if (event.target.value.length > 0 && document.activeElement === searchInputRef.current) {
            setShowPreview(true);
        } else if (event.target.value.length === 0) {
            setShowPreview(false);
        }
    };

    const handleCompanyClick = (company) => {
        isClickingPreviewItemRef.current = true;
        navigate(`/company/${company.id}`);
        setShowPreview(false);
        if (searchInputRef.current) {
            searchInputRef.current.blur();
        }
    };

    useEffect(() => {
        console.log('SearchBar useEffect - startDate:', startDate, 'endDate:', endDate);
        const searchParams = new URLSearchParams(location.search);
        const query = searchParams.get('q') || '';
        if (query) {
            fetchData(query);
        }
    }, [startDate, endDate, location.search]);

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    return (
        <div className='search-box-exterior'>
            <div className='search-box' ref={searchBoxRef}>
                <input 
                    className='search-bar-input' 
                    value={inputValue} 
                    onChange={handleInputChange} 
                    placeholder="Search for a company, topic, or theme!" 
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleSearch();
                        }
                    }}
                    ref={searchInputRef}
                />
                <button className='search-bar-button' onClick={handleSearch}>
                    <SearchIcon />
                </button>
            </div>
            {(
                <SearchPreviewNew 
                    query={inputValue} 
                    parentRef={searchInputRef}
                    anchorRef={searchBoxRef}
                    onlyShowCompanySearch={false}
                    handleClick={handleCompanyClick}
                    handleSearch={handleSearch}
                    onClear={() => setInputValue('')}
                    isVisible={showPreview}
                    isClickingPreviewItemRef={isClickingPreviewItemRef}
                />
            )}
        </div>
    );
}

export default SearchBar;
